import { h, Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import Storefront from 'utils/storefront';
import { productsByTag } from 'utils/storefront/queries';
import Image from 'utils/image';
import AngleRightIcon from 'utils/icon/icon-angle-right.svg';

const GroupedProducts = ({ groupTag, productHandle, departmentTag }) => {
  const storefront = Storefront.getInstance();
  const groupId = groupTag.split('group_id::')[1];
  const [products, setProducts] = useState(null);
  const [opened, setOpened] = useState(false);

  const getGroupedProducts = async (id) => {
    try {
      const response = await storefront.request({
        query: productsByTag(id),
      });
      if (!response.data) {
        // eslint-disable-next-line no-console
        console.error('Storefront API request failed', response);
        return {};
      }
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching products:', error);
      return {};
    }
  };

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const productData = await getGroupedProducts(groupId);
        const data = productData.products.nodes
          .map((node) => ({
            handle: node.handle,
            style: node.style?.reference.fields.find((el) => el.key === 'style')?.value || null,
            icon: node.style?.reference.fields.find((el) => el.key === 'style_icon')?.reference.image || null,
            hasOnlySaleVariants: node.tags.includes('hasOnlySaleVariants'),
            departmentTag: node.tags.find((el) => el.includes('department::')),
          }))
          .filter((product) => !product.hasOnlySaleVariants && product.departmentTag === departmentTag);

        const groupedProducts = data.reduce(
          (acc, el) => {
            if (el.handle === productHandle) {
              acc.activeProduct = el;
            } else {
              acc.restProducts.push(el);
            }
            return acc;
          },
          { activeProduct: null, restProducts: [] },
        );

        setProducts([groupedProducts.activeProduct, ...groupedProducts.restProducts]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching products:', error);
      }
    };

    if (groupId) {
      fetchProductData();
    }
  }, [groupId]);

  const handleClick = (e) => {
    e.preventDefault();
    setOpened(!opened);
  };

  if (!products?.[0]?.icon && !products?.[0]?.style) return '';

  return (
    <div className="max-w-[447px]">
      {products?.length > 1 && (
        <>
          <div className="body-14-med md:body-16-med text-smokey-blue mb-12">{window.TMW.Strings.product.style}</div>
          <button
            className={`body-14-med flex w-full items-center justify-between border border-primary bg-grey-6 p-[10px] text-faded-black md:px-15 ${opened ? 'rounded-t border-b-0' : 'rounded'} cursor-pointer`}
            onClick={(e) => handleClick(e)}
          >
            <div className="flex items-center gap-6">
              {products[0].icon && (
                <Image
                  src={products[0].icon.src}
                  classes="w-[20px] block object-fit"
                  alt={products[0].icon.alt}
                  width={products[0].icon.width}
                  height={products[0].icon.height}
                  srcWidths={{ sm: 100 }}
                />
              )}
              <span className="hover:underline">{products[0].style}</span>
            </div>
            <span className={`${opened ? '-rotate-90' : 'rotate-90'}`}>
              <AngleRightIcon />
            </span>
          </button>
        </>
      )}

      {products?.length > 1 && opened && (
        <div className="rounded-b border border-primary bg-grey-6">
          {products?.slice(1).map((product) => (
            <a
              href={product.handle}
              className="flex items-center gap-6 border-b border-grey-5 px-15 py-12 text-faded-black last-of-type:rounded-b hover:underline"
              key={product.handle}
            >
              {product.icon && (
                <Image
                  src={product.icon.src}
                  classes="w-[20px] block object-fit"
                  alt={product.icon.alt}
                  width={product.icon.width}
                  height={product.icon.height}
                  srcWidths={{ sm: 100 }}
                />
              )}
              {product.style}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupedProducts;
